import React, { useState, useEffect } from 'react';
import { X, UserCheck, Search, ChevronDown } from 'lucide-react';
import { employeesAPI } from '../../../services/api';

const TaskAssignModal = ({ count, onClose, onConfirm }) => {
  const [users, setUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(true);
  const [selectedUserId, setSelectedUserId] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [dropdownOpen, setDropdownOpen] = useState(false);

  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const response = await employeesAPI.getAllEmployees();
        const data = Array.isArray(response.data) ? response.data : response.data || [];
        const mapped = data
  .map(u => ({
    id: u.id,
    name: u.name || `${u.firstName || ''} ${u.lastName || ''}`.trim() || u.email || 'Unknown',
    roles: u.roles || [],
  }))
  .filter(u => u.name && u.name !== 'Unknown' && Array.isArray(u.roles) && u.roles.some(r => r === 'CRM' || r === 'ROLE_CRM'));
        setUsers(mapped);
      } catch (err) {
        console.error('Failed to fetch employees:', err);
      } finally {
        setLoadingUsers(false);
      }
    };
    fetchUsers();
  }, []);

  const filteredUsers = users.filter(u =>
    u.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const selectedUser = users.find(u => u.id === selectedUserId);

  const handleConfirm = () => {
    if (!selectedUserId) return;
    onConfirm(selectedUserId);
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-[2px] flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-sm w-full overflow-visible">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <h3 className="text-base font-semibold text-gray-900">Assign Tasks</h3>
          <button onClick={onClose} className="p-1 rounded-md hover:bg-gray-100 text-gray-400 transition-colors">
            <X size={16} />
          </button>
        </div>

        <div className="px-6 py-5">
          <div className="flex items-center gap-2 mb-4 text-sm text-gray-600">
            <UserCheck size={15} className="text-purple-500" />
            Assigning <span className="font-semibold text-gray-900 mx-1">{count}</span> task{count > 1 ? 's' : ''} to:
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Assignee</label>

            {loadingUsers ? (
              <div className="flex items-center justify-center h-10 rounded-lg bg-gray-50 border border-gray-200">
                <div className="w-4 h-4 border-2 border-purple-400 border-t-transparent rounded-full animate-spin" />
              </div>
            ) : (
              <div className="relative">
                {/* Dropdown trigger */}
                <button
                  type="button"
                  onClick={() => setDropdownOpen(prev => !prev)}
                  className="w-full flex items-center justify-between px-3 py-2 bg-white border border-gray-300 rounded-md text-sm text-left hover:border-purple-400 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-colors"
                >
                  <span className={selectedUser ? 'text-gray-900' : 'text-gray-400'}>
                    {selectedUser ? selectedUser.name : 'Select a team member...'}
                  </span>
                  <ChevronDown
                    size={15}
                    className={`text-gray-400 transition-transform ${dropdownOpen ? 'rotate-180' : ''}`}
                  />
                </button>

                {/* Dropdown panel */}
                {dropdownOpen && (
                  <div className="absolute z-50 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-xl overflow-hidden">
                    {/* Search */}
                    <div className="flex items-center gap-2 px-3 py-2 border-b border-gray-100 bg-white sticky top-0 z-10">
                      <Search size={13} className="text-gray-400 flex-shrink-0" />
                      <input
                        type="text"
                        placeholder="Search members..."
                        value={searchQuery}
                        onChange={e => setSearchQuery(e.target.value)}
                        className="flex-1 text-sm outline-none placeholder:text-gray-400"
                        autoFocus
                      />
                      {searchQuery && (
                        <button onClick={() => setSearchQuery('')} className="text-gray-400 hover:text-gray-600">
                          <X size={12} />
                        </button>
                      )}
                    </div>

                    {/* List */}
                    <ul style={{ maxHeight: '400px', overflowY: 'auto' }} className="py-1">
                      {filteredUsers.length === 0 ? (
                        <li className="px-4 py-3 text-sm text-gray-400 text-center">No members found</li>
                      ) : (
                        filteredUsers.map(user => (
                          <li key={user.id}>
                            <button
                              type="button"
                              onClick={() => {
                                setSelectedUserId(user.id);
                                setDropdownOpen(false);
                                setSearchQuery('');
                              }}
                              className={`w-full flex items-center gap-3 px-3 py-2 text-sm text-left hover:bg-purple-50 transition-colors ${
                                selectedUserId === user.id
                                  ? 'bg-purple-50 text-purple-700 font-medium'
                                  : 'text-gray-700'
                              }`}
                            >
                              <div className="w-6 h-6 rounded-full bg-gradient-to-br from-purple-400 to-indigo-400 flex items-center justify-center text-white text-xs font-bold flex-shrink-0">
                                {user.name.charAt(0).toUpperCase()}
                              </div>
                              {user.name}
                            </button>
                          </li>
                        ))
                      )}
                    </ul>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        <div className="flex items-center justify-end gap-3 px-6 py-4 bg-gray-50 border-t border-gray-100 rounded-b-2xl">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm border border-gray-200 rounded-lg hover:bg-gray-100 transition-colors font-medium text-gray-600"
          >
            Cancel
          </button>
          <button
            onClick={handleConfirm}
            disabled={!selectedUserId}
            className="px-4 py-2 text-sm bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Assign
          </button>
        </div>
      </div>
    </div>
  );
};

export default TaskAssignModal;